import { humanize, Icon, Pill, StateBox } from '@garnet/ui';
import { useAsync } from '../useAsync.js';
import type { InspectorApi, InspectorProfile, PastAttestation } from '../data/types.js';
import { CardLabel } from '../components/PhoneChrome.js';

const OUTCOME_TONE: Record<PastAttestation['outcome'], 'm' | 'a' | 'f'> = {
  confirmed: 'm',
  inconclusive: 'a',
  refuted: 'f',
};

const STANDING_TONE: Record<InspectorProfile['standing'], 'm' | 'a' | 'f'> = {
  active: 'm',
  probation: 'a',
  suspended: 'f',
};

/** Sync status carried by an icon AND a label, never colour alone (CONVENTIONS §13). */
function SyncBadge({ synced }: { synced: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: synced ? 'var(--verified-ink)' : 'var(--muted)',
      }}
    >
      <Icon name={synced ? 'check' : 'clock'} size={12} />
      {synced ? 'synced' : 'queued'}
    </span>
  );
}

/** History — past signed attestations and the inspector's standing/coverage (PRD §14.3, §10.1). */
export function History({ api, profile }: { api: InspectorApi; profile: InspectorProfile }) {
  const { data, loading, error } = useAsync(() => api.getHistory());

  if (loading) return <StateBox kind="loading" title="Loading history" />;
  if (error || !data) return <StateBox kind="error" title="Could not load history">{error}</StateBox>;

  const fmt = (iso: string): string => new Date(iso).toISOString().slice(0, 10);

  return (
    <div style={{ padding: 14, display: 'grid', gap: 14 }}>
      <div style={{ background: 'var(--panel)', border: '1px solid var(--rule)', borderRadius: 10, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div>
            <CardLabel>{profile.id} · {humanize(profile.kind)}</CardLabel>
            <div style={{ fontWeight: 600, fontSize: 15, marginTop: 4 }}>{profile.name}</div>
          </div>
          <Pill tone={STANDING_TONE[profile.standing]}>{profile.standing}</Pill>
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 12, fontFamily: 'var(--mono)', fontSize: 12 }}>
          <span>
            <span style={{ color: 'var(--muted)' }}>SLA </span>
            {Math.round(profile.rating.sla_hit_rate * 100)}%
          </span>
          <span>
            <span style={{ color: 'var(--muted)' }}>disputes </span>
            {Math.round(profile.rating.dispute_rate * 100)}%
          </span>
        </div>
        <div style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
          coverage · {profile.coverage.jurisdictions.join(', ')} · {profile.coverage.methods.map(humanize).join(', ')}
        </div>
      </div>

      <div>
        <CardLabel style={{ padding: '0 2px 8px' }}>past attestations · {data.length}</CardLabel>
        {data.length === 0 ? (
          <StateBox kind="empty" title="No attestations yet">
            Signed attestations you submit will be listed here.
          </StateBox>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {data.map((a) => (
              <div key={a.id} style={{ background: 'var(--panel)', border: '1px solid var(--rule)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <CardLabel>{a.id} · {a.method_label}</CardLabel>
                  <Pill tone={OUTCOME_TONE[a.outcome]}>{a.outcome}</Pill>
                </div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{a.asset}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
                  {a.supplier} · resolved {a.resolves_check_label}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2, fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)' }}>
                  <span>signed {fmt(a.signed_at)}</span>
                  <SyncBadge synced={a.synced} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
